import _ from 'lodash';
import {initValidationsServiceState, getCompValidationState, setCompValidationState, unsetCompValidationState} from './validationsServiceState';
import {getCustomValidity, validateValue, addCustomValidityToValidateData} from '../utils/valueValidation';
import {getCheckedValues} from '../utils/checkboxGroupUtils';
import {generateError} from '../validations/errors';
import platformUtils from '../platformUtils';

import reporter from '../reporter';

let customValidations = {};

const initValidationsService = () => {
    customValidations = {};
    initValidationsServiceState();
};

const getValueMethodToInputType = {
    CheckboxInput: (RMI, compId) => RMI.getData(compId).checked,
    CheckboxGroup: (RMI, compId) => getCheckedValues(RMI.getData(compId).options),
    DatePicker: (RMI, compId) => RMI.getState(compId).selectedDate,
    FileUploader: (RMI, compId) => RMI.getState(compId).value
};

function extendValueMethodToInputType(type, getValueMethod) {
    if(getValueMethodToInputType[type]){
        reporter.reportSdkError(generateError.error_component_already_exists({component: type}));
        return false;
    }

    _.set(getValueMethodToInputType, type, getValueMethod);
    return true;
}

const getValueByInputType = (RMI, compId) => {
    const compData = RMI.getData(compId);
    const getValueMethod = getValueMethodToInputType[compData.type];

    if (getValueMethod){
        return getValueMethod(RMI, compId);
    }

    return compData.value;
};

const onCustomValidation = (RMI, compId, validator) => {
    customValidations[compId] = validator;

    runValidations(RMI, compId);
};

const checkCustomValidity = (compId, value) => {
    const customValidator = customValidations[compId];
    let customValidity = '';

    const reject = (message) => {
        customValidity = message;
    };

    if (customValidator){
        customValidator(value, reject);
    }

    return customValidity;
};

const updateValidityIndication = (RMI, compId, indicationValue) => {
    const state = RMI.getState(compId);
    const shouldShowValidationIndication = _.get(state, 'shouldShowValidationIndication', false);

    if (shouldShowValidationIndication !== indicationValue){
        RMI.updateModel({
            [compId]: {
                state: {
                    shouldShowValidationIndication: indicationValue
                }
            }
        });

        RMI.executeCompBehavior(compId, 'updateValidityIndication', {value: indicationValue, requiresDom: null});
    }
};

const showValidityIndication = (RMI, compId) => updateValidityIndication(RMI, compId, true);
const hideValidityIndication = (RMI, compId) => updateValidityIndication(RMI, compId, false);

const runValidations = (RMI, compId, value) => {
    value = !_.isUndefined(value) ? value : getValueByInputType(RMI, compId);
    const validationState = getCompValidationState(compId);
    const prevCustomValidity = getCustomValidity(validationState);
    const compData = RMI.getFullDataWithOverrides(compId);
    const compProps = RMI.getProps(compId);
    const compState = RMI.getState(compId);

    let validateData = validateValue(value, compData, compProps, compState);
    const customValidity = checkCustomValidity(compId, value);

    if (customValidity){
        validateData = addCustomValidityToValidateData(validateData, customValidity);
    }

    setCompValidationState(compId, validateData);

    if (customValidity !== prevCustomValidity) {
        RMI.executeCompBehavior(compId, RMI.getEventTypes().SET_CUSTOM_VALIDITY, { customValidity, requiresDom: null });
    }
};

const clearValidations = (RMI, compId) => {
    unsetCompValidationState(compId);
};

const runValidationsAfterChange = (RMI, compId, value) => {
    if (platformUtils.getRGI().getLivePreviewMode && platformUtils.getRGI().getLivePreviewMode()){
       return;
    }
    runValidations(RMI, compId, value);
    showValidityIndication(RMI, compId);
};

const runValidationsAndSync = (RMI, compId, value) => {
    if (platformUtils.getRGI().getLivePreviewMode && platformUtils.getRGI().getLivePreviewMode()){
        return;
    }
    value = !_.isUndefined(value) ? value : getValueByInputType(RMI, compId);
    const compData = RMI.getFullDataWithOverrides(compId);
    const compProps = RMI.getProps(compId);
    const compState = RMI.getState(compId);

    let validateData = validateValue(value, compData, compProps, compState);
    const customValidity = checkCustomValidity(compId, value);

    if (customValidity){
        validateData = addCustomValidityToValidateData(validateData, customValidity);
    }

    setCompValidationState(compId, validateData);

    RMI.executeCompBehavior(compId, RMI.getEventTypes().SYNC_VALIDATION_DATA, { validateData, requiresDom: null });
};

const runValidationsAndSyncAfterChange = (RMI, compId, value) => {
    if (platformUtils.getRGI().getLivePreviewMode && platformUtils.getRGI().getLivePreviewMode()){
        return;
    }
    runValidationsAndSync(RMI, compId, value);
    showValidityIndication(RMI, compId);
};

const getValidity = (compId) => _.get(getCompValidationState(compId), 'validity');

const isValid = (compId) => _.get(getCompValidationState(compId), 'validity.valid', true);

const getValidationMessage = (compId) => _.get(getCompValidationState(compId), 'validationMessage', '');

export {
    initValidationsService,
    onCustomValidation,
    runValidations,
    runValidationsAndSync,
    getValidity,
    isValid,
    showValidityIndication,
    hideValidityIndication,
    runValidationsAfterChange,
    runValidationsAndSyncAfterChange,
    getValidationMessage,
    clearValidations,
    extendValueMethodToInputType
};
